import {
  removeQueueMutation,
  markDraftSynced,
  markDraftError,
  getPendingQueueItems,
  getDb
} from "./countDraftDb";
import { META_INVENTORY_KEY } from "./countDraftConstants";
import logger from "../utils/logger";

/** Per-item POST budget. Prevents a hung XHR from holding the queue lock forever. */
export const DRAFT_ITEM_REQUEST_TIMEOUT_MS = 30000;
/** If processing stays true longer than this, treat the lock as stale and allow a fresh run. */
export const DRAFT_SYNC_STALE_LOCK_MS = 45000;
/** Cap exponential backoff for retriable failures (keep queue row; do not drop). */
const MAX_RETRY_BACKOFF_MS = 60000;

let processing = false;
let processingStartedAt = 0;
let runId = 0;
let activeController = null;

let connectivityBound = false;
let connectivityRunner = null;

export function isCountDraftSyncProcessing() {
  return processing;
}

/** Test-only: reset module lock / generation / abort controller. */
export function __resetCountDraftSyncStateForTests() {
  processing = false;
  processingStartedAt = 0;
  runId = 0;
  if (activeController) {
    try {
      activeController.abort();
    } catch (_) {
      /* ignore */
    }
  }
  activeController = null;
}

/**
 * Decide whether a new queue run may start.
 * force / stale lock → abort previous in-flight request and start fresh.
 */
export function shouldStartQueueRun({
  processing: isProcessing,
  processingStartedAt: startedAt,
  force,
  now,
  staleLockMs
}) {
  if (!isProcessing) return { start: true, abortPrevious: false, reason: "idle" };
  const stale = startedAt > 0 && now - startedAt > staleLockMs;
  if (force || stale) {
    return {
      start: true,
      abortPrevious: true,
      reason: force ? "force" : "stale_lock"
    };
  }
  return { start: false, abortPrevious: false, reason: "busy" };
}

export function classifyDraftSyncError(err) {
  const status = err && err.response && err.response.status;
  const code = err && err.code;
  const name = err && err.name;
  const msg = (err && err.message) || "";
  const aborted =
    code === "ERR_CANCELED" ||
    code === "ECONNABORTED" ||
    name === "CanceledError" ||
    name === "AbortError" ||
    /timeout/i.test(msg) ||
    /aborted/i.test(msg);

  if (!err || !err.response) {
    return {
      retriable: true,
      kind: aborted ? "timeout" : "network",
      message: aborted ? "Request timed out" : msg || "Network error"
    };
  }
  if (status >= 500) return { retriable: true, kind: "server", message: "Server error" };
  if (status === 401 || status === 403) {
    return { retriable: false, kind: "auth", message: "Session expired" };
  }
  if (status === 400 || status === 404 || status === 409 || status === 422) {
    return {
      retriable: false,
      kind: "rejected",
      message: (err.response.data && err.response.data.error) || "Request rejected"
    };
  }
  return { retriable: false, kind: "other", message: msg || "Sync failed" };
}

/** @deprecated use classifyDraftSyncError */
function classifyError(err) {
  return classifyDraftSyncError(err);
}

/** Safe structured log for support (no auth headers). */
function logDraftItemRequestFailure(err, item, payload, classified) {
  const status = err.response != null ? err.response.status : null;
  let responseBody = err.response != null ? err.response.data : undefined;
  if (responseBody != null && typeof responseBody === "object") {
    try {
      responseBody = JSON.stringify(responseBody);
    } catch (_) {
      responseBody = String(responseBody);
    }
  } else if (responseBody != null) {
    responseBody = String(responseBody);
  }
  const maxLen = 2000;
  if (typeof responseBody === "string" && responseBody.length > maxLen) {
    responseBody = responseBody.slice(0, maxLen) + "…";
  }
  logger.warn("[countDraftSync] POST /api/tenant/site/draft-counts/item failed", {
    httpStatus: status,
    responseBody: responseBody != null ? responseBody : null,
    retriable: classified.retriable,
    kind: classified.kind,
    classifiedMessage: classified.message,
    mutationIdShort: String(item.mutationId || "").slice(0, 8),
    inventoryItemId: payload.inventoryItemId != null ? String(payload.inventoryItemId) : null,
    currentPeriodKey: payload.currentPeriodKey != null ? String(payload.currentPeriodKey) : null,
    errMessage: err && err.message ? String(err.message) : null
  });
}

function abortActiveRequest() {
  if (!activeController) return;
  try {
    activeController.abort();
  } catch (_) {
    /* ignore */
  }
  activeController = null;
}

/**
 * Bind once; always invoke the latest runner (avoids stale runSync closures).
 * online / visibility / focus all trigger retry while local unsynced data remains.
 */
export function bindConnectivityHandlers(runSync) {
  connectivityRunner = runSync;
  if (typeof window === "undefined" || connectivityBound) return;
  connectivityBound = true;
  const invoke = () => {
    if (typeof connectivityRunner === "function") connectivityRunner();
  };
  window.addEventListener("online", invoke);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") invoke();
  });
  window.addEventListener("focus", invoke);
}

/** Callers pass `currentPeriodKey`; keep both names so queue scope matches IndexedDB rows. */
export function resolveQueuePeriodKey(scope) {
  if (!scope) return undefined;
  return scope.periodKey !== undefined ? scope.periodKey : scope.currentPeriodKey;
}

/**
 * Process pending draft-line mutations for one screen scope.
 * options.force — abort any in-flight run and start a fresh upload (Retry sync).
 */
export async function processCountDraftQueue(axiosInstance, scope, options) {
  const tenantId = scope.tenantId;
  const userId = scope.userId;
  const siteId = scope.siteId;
  const periodKey = resolveQueuePeriodKey(scope);
  const force = !!(options && options.force);
  if (!tenantId || !userId || !siteId || periodKey === undefined || periodKey === null) {
    return { started: false, reason: "invalid_scope" };
  }

  const decision = shouldStartQueueRun({
    processing,
    processingStartedAt,
    force,
    now: Date.now(),
    staleLockMs: DRAFT_SYNC_STALE_LOCK_MS
  });
  if (!decision.start) {
    return { started: false, reason: decision.reason };
  }
  if (decision.abortPrevious) {
    abortActiveRequest();
  }

  const db = await getDb();
  if (!db) return { started: false, reason: "no_db" };

  const myRun = ++runId;
  processing = true;
  processingStartedAt = Date.now();

  try {
    const pending = await getPendingQueueItems(tenantId, userId, siteId, periodKey);
    if (pending.length > 0) {
      logger.warn("[countDraftSync] replay start", { count: pending.length, force, reason: decision.reason });
    }
    pending.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    for (const item of pending) {
      if (myRun !== runId) {
        return { started: true, superseded: true };
      }
      const payload = item.payload || {};
      const body = {
        clientMutationId: payload.clientMutationId,
        siteId: payload.siteId,
        currentPeriodKey: payload.currentPeriodKey,
        inventoryItemId: payload.inventoryItemId,
        quantity: payload.quantity,
        quantity2: payload.quantity2,
        notes: payload.notes,
        personName: payload.personName,
        generalNotes: payload.generalNotes,
        updatedAt: payload.updatedAt
      };
      const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      activeController = controller;
      let timer = null;
      if (controller) {
        timer = setTimeout(() => {
          try {
            controller.abort();
          } catch (_) {
            /* ignore */
          }
        }, DRAFT_ITEM_REQUEST_TIMEOUT_MS);
      }
      try {
        const reqConfig = {
          timeout: DRAFT_ITEM_REQUEST_TIMEOUT_MS
        };
        if (controller) reqConfig.signal = controller.signal;
        const res = await axiosInstance.post("/api/tenant/site/draft-counts/item", body, reqConfig);
        if (myRun !== runId) {
          return { started: true, superseded: true };
        }
        const draft = res.data && res.data.draft;
        const srvUpdated = draft && draft.updatedAt;
        const inv = payload.inventoryItemId;
        if (inv && inv !== META_INVENTORY_KEY) {
          await markDraftSynced(tenantId, userId, siteId, periodKey, inv, srvUpdated);
        } else {
          await markDraftSynced(tenantId, userId, siteId, periodKey, META_INVENTORY_KEY, srvUpdated);
        }
        await removeQueueMutation(item.mutationId);
        logger.warn("[countDraftSync] item ok", { mutationIdShort: String(item.mutationId).slice(0, 8) });
        if (options && options.onProgress) options.onProgress({ type: "ok", mutationId: item.mutationId });
      } catch (err) {
        if (myRun !== runId) {
          return { started: true, superseded: true };
        }
        const classified = classifyError(err);
        const { retriable, message, kind } = classified;
        logDraftItemRequestFailure(err, item, payload, classified);
        const inv = payload.inventoryItemId;
        if (inv && inv !== META_INVENTORY_KEY) {
          await markDraftError(tenantId, userId, siteId, periodKey, inv, message);
        } else {
          await markDraftError(tenantId, userId, siteId, periodKey, META_INVENTORY_KEY, message);
        }
        const rc = (item.retryCount || 0) + 1;
        const nextRetry = new Date(
          Date.now() + Math.min(MAX_RETRY_BACKOFF_MS, 2000 * Math.pow(2, Math.min(rc - 1, 5)))
        ).toISOString();

        if (kind === "auth") {
          // Keep queue row so a fresh session can upload; stop this pass (do not spin on 401/403).
          await db.syncQueue.update(item.mutationId, {
            retryCount: rc,
            nextRetryAt: nextRetry,
            lastError: message
          });
          if (options && options.onProgress) options.onProgress({ type: "err", message, kind });
          break;
        }

        if (retriable) {
          // Never drop retriable queue rows — keep retrying with capped backoff.
          await db.syncQueue.update(item.mutationId, {
            retryCount: rc,
            nextRetryAt: nextRetry,
            lastError: message
          });
        } else {
          // Permanent client/validation rejection — remove queue row; draft stays in error for Retry sync.
          await removeQueueMutation(item.mutationId);
        }
        if (options && options.onProgress) options.onProgress({ type: "err", message, kind });
        if (!retriable) continue;
      } finally {
        if (timer) clearTimeout(timer);
        if (activeController === controller) activeController = null;
      }
    }
    return { started: true, reason: decision.reason };
  } finally {
    if (myRun === runId) {
      processing = false;
      processingStartedAt = 0;
      activeController = null;
    }
  }
}
